import React, { useState } from 'react';
import './KYCDemo.css';

const KYCDemo = () => {
  const [demoStep, setDemoStep] = useState(1);
  const [demoData, setDemoData] = useState({
    merchantId: 'DEMO123456',
    fullName: 'John Doe',
    businessName: 'Demo Business Pvt Ltd',
    panNumber: 'ABCDE1234F',
    gstin: '22AAAAA0000A1Z5',
    bankAccount: '1234567890123456'
  });

  const demoSteps = [
    {
      step: 1,
      title: 'Business Information',
      description: 'Enter basic business details',
      status: 'completed'
    },
    {
      step: 2,
      title: 'PAN & GST Verification',
      description: 'Real-time verification of tax documents',
      status: 'completed'
    },
    {
      step: 3,
      title: 'Address Information',
      description: 'Registered business address',
      status: 'completed'
    },
    {
      step: 4,
      title: 'Bank Verification',
      description: 'Penny drop verification',
      status: 'in-progress'
    },
    {
      step: 5,
      title: 'Document Upload',
      description: 'Upload supporting documents',
      status: 'pending'
    },
    {
      step: 6,
      title: 'Review & Submit',
      description: 'Final review and submission',
      status: 'pending'
    }
  ];

  const apiEndpoints = [
    {
      method: 'POST',
      endpoint: '/api/kyc/initiate',
      description: 'Initialize KYC process for merchant',
      params: 'merchantId'
    },
    {
      method: 'GET',
      endpoint: '/api/kyc/status/{merchantId}',
      description: 'Get current KYC status and progress',
      params: 'merchantId'
    },
    {
      method: 'PUT',
      endpoint: '/api/kyc/update',
      description: 'Update KYC information',
      params: 'merchantId, kycData'
    },
    {
      method: 'POST',
      endpoint: '/api/kyc/verify-pan',
      description: 'Verify PAN number in real-time',
      params: 'merchantId, panNumber'
    },
    {
      method: 'POST',
      endpoint: '/api/kyc/verify-gst',
      description: 'Verify GST number',
      params: 'merchantId, gstin'
    },
    {
      method: 'POST',
      endpoint: '/api/kyc/verify-bank',
      description: 'Perform penny drop verification',
      params: 'merchantId, bankDetails'
    },
    {
      method: 'POST',
      endpoint: '/api/documents/upload',
      description: 'Upload KYC documents',
      params: 'merchantId, file, documentType'
    },
    {
      method: 'POST',
      endpoint: '/api/kyc/submit',
      description: 'Submit KYC for final review',
      params: 'merchantId'
    }
  ];

  const features = [
    {
      icon: '⚡',
      title: 'Real-time Verification',
      description: 'Instant PAN, GST, and bank account verification using government APIs'
    },
    {
      icon: '📱',
      title: 'Mobile Responsive',
      description: 'Complete KYC process on any device with responsive design'
    },
    {
      icon: '🔒',
      title: 'Secure & Compliant',
      description: 'Bank-grade security with RBI and GDPR compliance'
    },
    {
      icon: '📊',
      title: 'Progress Tracking',
      description: 'Real-time progress updates and status notifications'
    },
    {
      icon: '📄',
      title: 'Document Management',
      description: 'Secure document upload with preview and validation'
    },
    {
      icon: '🎯',
      title: 'Smart Validation',
      description: 'Intelligent form validation with helpful error messages'
    }
  ];

  return (
    <div className="kyc-demo-container">
      <div className="demo-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>🚀 Complete KYC Solution</h1>
            <p>A comprehensive Know Your Customer (KYC) interface with backend API integration</p>
          </div>
          <button onClick={() => window.location.href = '/dashboard'}
            style={{ padding: '10px 20px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            ← Back to Dashboard
          </button>
        </div>
      </div>

      <div className="demo-overview">
        <div className="overview-card">
          <h2>📋 What's Included</h2>
          <div className="included-items">
            <div className="item">✅ Multi-step KYC form with progress tracking</div>
            <div className="item">✅ Real-time PAN, GST & bank verification</div>
            <div className="item">✅ Secure document upload with preview</div>
            <div className="item">✅ Status dashboard with notifications</div>
            <div className="item">✅ Comprehensive help & support system</div>
            <div className="item">✅ Mobile-responsive design</div>
            <div className="item">✅ Complete API integration</div>
          </div>
        </div>

        <div className="demo-stats">
          <div className="stat-card">
            <div className="stat-number">6</div>
            <div className="stat-label">KYC Steps</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">8</div>
            <div className="stat-label">API Endpoints</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">15+</div>
            <div className="stat-label">Components</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">100%</div>
            <div className="stat-label">Mobile Ready</div>
          </div>
        </div>
      </div>

      <div className="demo-sections">
        <div className="demo-section">
          <h2>🔄 KYC Process Flow</h2>
          <div className="process-flow">
            {demoSteps.map((step, index) => (
              <div key={step.step} className={`flow-step ${step.status}`}>
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                  <span className={`status-badge ${step.status}`}>
                    {step.status === 'completed' && '✅ Completed'}
                    {step.status === 'in-progress' && '⏳ In Progress'}
                    {step.status === 'pending' && '⭕ Pending'}
                  </span>
                </div>
                {index < demoSteps.length - 1 && <div className="flow-arrow">→</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="demo-section">
          <h2>🔌 API Integration</h2>
          <div className="api-list">
            {apiEndpoints.map((api, index) => (
              <div key={index} className="api-item">
                <div className="api-method">{api.method}</div>
                <div className="api-details">
                  <div className="api-endpoint">{api.endpoint}</div>
                  <div className="api-description">{api.description}</div>
                  <div className="api-params">Parameters: {api.params}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="demo-section">
          <h2>✨ Key Features</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="demo-section">
          <h2>🎯 Demo Data</h2>
          <div className="demo-data">
            <div className="data-card">
              <h4>Sample Merchant Data</h4>
              <div className="data-items">
                <div className="data-item">
                  <span className="data-label">Merchant ID:</span>
                  <span className="data-value">{demoData.merchantId}</span>
                </div>
                <div className="data-item">
                  <span className="data-label">Full Name:</span>
                  <span className="data-value">{demoData.fullName}</span>
                </div>
                <div className="data-item">
                  <span className="data-label">Business Name:</span>
                  <span className="data-value">{demoData.businessName}</span>
                </div>
                <div className="data-item">
                  <span className="data-label">PAN Number:</span>
                  <span className="data-value">{demoData.panNumber}</span>
                </div>
                <div className="data-item">
                  <span className="data-label">GSTIN:</span>
                  <span className="data-value">{demoData.gstin}</span>
                </div>
                <div className="data-item">
                  <span className="data-label">Bank Account:</span>
                  <span className="data-value">{demoData.bankAccount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="demo-actions">
          <h2>🚀 Get Started</h2>
          <div className="action-buttons">
            <button 
              className="btn btn-primary btn-lg"
              onClick={() => window.location.href = '/kyc'}
            >
              Start KYC Process
            </button>
            <button 
              className="btn btn-secondary btn-lg"
              onClick={() => window.location.href = '/kyc/help'}
            >
              View Documentation
            </button>
            <button 
              className="btn btn-outline btn-lg"
              onClick={() => window.location.href = '/dashboard'}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYCDemo;